import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { playerContext } from 'contexts/player-contenxt.js';
import {
  developmentMode,
  safeZoneContainer,
  safeZoneSpacer,
} from './SafeZone.module.css';

let className = safeZoneContainer;
if (process.env.NODE_ENV === 'development') {
  className += ` ${developmentMode}`;
}

/**
 * Spacer component for safe zone size changes detection and propagate it to player.
 * @param skinHidden
 * @param zoneId
 * @return {Element}
 * @constructor
 */
export function Spacer({ skinHidden, zoneId }) {
  const { API } = useContext(playerContext);
  const ref = useRef(null);
  const [zoneOffsets, setZoneOffests] = React.useState({ top: 0, bottom: 0 });

  const getZoneParameters = useCallback(() => {
    return getElementRectV2(ref.current, {
      root: ref.current.parentNode,
    })
      .then((entry) => {
        const top = Math.floor(
          entry.boundingClientRect.top - entry.rootBounds.top
        );
        const bottom = Math.floor(
          entry.rootBounds.bottom - entry.boundingClientRect.bottom
        );

        return {
          top,
          bottom,
        };
      })
      .catch((error) => {
        console.error(error);
        return { top: 0, bottom: 0 };
      });
  }, []);

  const applyZoneParameters = useCallback(
    ({ top, bottom }) => {
      API.emitSafeZoneSizeChanged([
        { id: zoneId, top, bottom, left: 0, right: 0 },
      ]);
    },
    [API, zoneId]
  );

  useEffect(() => {
    getZoneParameters().then(({ top, bottom }) => {
      setZoneOffests({ top, bottom });
      applyZoneParameters({ top, bottom });
    });
  }, [getZoneParameters, applyZoneParameters]);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      getZoneParameters().then(({ top, bottom }) => {
        setZoneOffests((prevOffsets) => {
          if (prevOffsets.top === top && prevOffsets.bottom === bottom) {
            return prevOffsets; // No change, no need to update
          }
          return { top, bottom };
        });
      });
    });
    resizeObserver.observe(ref.current);
    return () => {
      resizeObserver.disconnect();
    };
  }, [getZoneParameters]);

  useEffect(() => {
    if (skinHidden) {
      applyZoneParameters({ top: 0, bottom: 0 });
    } else {
      applyZoneParameters(zoneOffsets);
    }
  }, [applyZoneParameters, skinHidden, zoneOffsets]);

  return <section className={safeZoneSpacer} ref={ref} />;
}

/**
 * Component for holding interactive proxy controls. Must reflect safe zone spacer sizes.
 * @param {string} zoneId
 * @return {Element}
 * @constructor
 */
export function Container({ zoneId }) {
  const { properties } = useContext(playerContext);
  const ref = useRef(null);

  useEffect(() => {
    if (!properties.interactiveZone) {
      return;
    }

    const zone = properties.interactiveZone.find((zone) => zone.id === zoneId);

    if (!zone) {
      return;
    }

    ref.current.style.borderWidth = `${zone.top}px 0 ${zone.bottom}px`;
  }, [properties.interactiveZone, zoneId]);

  return <article id={zoneId} ref={ref} className={className} />;
}
function getElementRectV2(element, options) {
  return new Promise((resolve, reject) => {
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        clearTimeout(emergencyTimeout);
        resolve(entry);
      }

      observer.disconnect();
    }, options);
    const emergencyTimeout = setTimeout(() => {
      observer.disconnect();
      const error = new Error('Timeout: getting element rect');
      reject(error);
    }, 120);

    observer.observe(element);
  });
}
